import React, { useEffect, useState } from 'react';
import { BadgeCheck, Loader2, XCircle, CheckCircle2, Clock, Building2, ShieldCheck, FileText, ExternalLink, RefreshCw } from 'lucide-react';
import { adminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
};

export default function CourierApplicationsPage() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [acting, setActing] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = async (filter = statusFilter) => {
    setLoading(true);
    try { setApps(await adminApi.courierApplications(filter)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load applications'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(statusFilter); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const approve = async (app) => {
    setActing(app.application_id);
    try {
      await adminApi.approveCourierApplication(app.application_id);
      toast.success(`${app.driver_name || 'Driver'} is now a verified COURIER`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to approve'); }
    finally { setActing(null); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Please provide a rejection reason'); return; }
    setActing(rejectTarget.application_id);
    try {
      await adminApi.rejectCourierApplication(rejectTarget.application_id, rejectReason.trim());
      toast.success('Application rejected — driver notified');
      setRejectTarget(null); setRejectReason('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to reject'); }
    finally { setActing(null); }
  };

  return (
    <div data-testid="courier-applications-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <BadgeCheck className="h-6 w-6 text-indigo-600" /> Courier applications
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Review business registration + insurance documents. Approval grants the COURIER badge and unlocks
            same-day packages, courier and business-logistics orders.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="courier-apps-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => load()} data-testid="courier-apps-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : apps.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white py-14 text-center text-slate-500" data-testid="courier-apps-empty">
          No {statusFilter === 'all' ? '' : statusFilter} courier applications.
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {apps.map((a) => (
            <div key={a.application_id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`courier-app-card-${a.application_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    {a.driver_name || 'Unknown driver'}
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLES[a.status] || 'bg-slate-100 text-slate-700'}`} data-testid={`courier-app-status-${a.application_id}`}>
                      {a.status}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {a.driver_email} {a.driver_phone ? `· ${a.driver_phone}` : ''} · Submitted {a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '—'}
                  </div>
                  {a.status === 'rejected' && a.rejection_reason && (
                    <div className="text-xs text-rose-600 mt-1">Rejection reason: {a.rejection_reason}</div>
                  )}
                </div>
                {a.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={acting === a.application_id}
                      onClick={() => approve(a)} data-testid={`courier-app-approve-${a.application_id}`}>
                      {acting === a.application_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Approve
                    </Button>
                    <Button size="sm" variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50" disabled={acting === a.application_id}
                      onClick={() => { setRejectTarget(a); setRejectReason(''); }} data-testid={`courier-app-reject-${a.application_id}`}>
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                )}
                {a.status === 'pending' && <Clock className="hidden" />}
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <Info icon={Building2} label="Business name" value={a.business_name} />
                <Info icon={FileText} label="Registration #" value={a.business_registration_number} />
                <Info icon={ShieldCheck} label="Insurance provider" value={a.business_insurance_provider} />
                <Info icon={ShieldCheck} label="Policy #" value={a.business_insurance_policy_number} />
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DocPreview label="Business registration document" url={a.business_registration_doc_url} testId={`courier-app-regdoc-${a.application_id}`} />
                <DocPreview label="Business insurance document" url={a.business_insurance_doc_url} testId={`courier-app-insdoc-${a.application_id}`} />
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject courier application</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Tell <b>{rejectTarget?.driver_name}</b> why their application was rejected. They can correct their documents and re-apply.
          </p>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Insurance document is expired — please upload a current policy." rows={3}
            data-testid="courier-app-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={reject} disabled={acting === rejectTarget?.application_id}
              data-testid="courier-app-reject-confirm">
              {acting === rejectTarget?.application_id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reject & notify driver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</div>
    <div className="font-medium text-slate-900 mt-0.5 truncate">{value || '—'}</div>
  </div>
);

const DocPreview = ({ label, url, testId }) => (
  <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid={testId}>
    <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs font-medium text-slate-600">
      {label}
      {url && (
        <a href={url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">
          Open <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
    {url ? (
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={label} className="w-full h-44 object-contain bg-slate-100" />
      </a>
    ) : (
      <div className="h-24 flex items-center justify-center text-xs text-slate-400">No document uploaded</div>
    )}
  </div>
);
